document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-faq-button]');
  if (button) {
    const item = button.closest('.wuvo-faq__item, .wuvo-faq-preview__item');
    if (!item) return;
    const list = item.closest('.wuvo-faq__list, .wuvo-faq-preview__list');
    const willOpen = !item.classList.contains('is-open');

    if (willOpen && list) {
      list.querySelectorAll('.wuvo-faq__item.is-open, .wuvo-faq-preview__item.is-open').forEach((openItem) => {
        openItem.classList.remove('is-open');
        openItem.querySelector('[data-faq-button]')?.setAttribute('aria-expanded', 'false');
      });
    }

    item.classList.toggle('is-open', willOpen);
    button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    list?.classList.toggle('has-open', Boolean(list.querySelector('.wuvo-faq__item.is-open, .wuvo-faq-preview__item.is-open')));
    return;
  }

  const menuButton = event.target.closest('.wuvo-menu');
  if (menuButton) {
    const header = menuButton.closest('.wuvo-header');
    const open = header.classList.toggle('is-menu-open');
    menuButton.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
});

function getDefaultCountdownEnd(now) {
  return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
}

function initialiseAnnouncement(announcement) {
  if (announcement.dataset.wuvoAnnouncementReady === 'true') return;

  const track = announcement.querySelector('[data-wuvo-announcement-track]');
  const slides = Array.from(announcement.querySelectorAll('[data-wuvo-announcement-slide]'));
  if (!track || slides.length < 2) return;

  announcement.dataset.wuvoAnnouncementReady = 'true';
  const previousButton = announcement.querySelector('[data-wuvo-announcement-prev]');
  const nextButton = announcement.querySelector('[data-wuvo-announcement-next]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const parsedInterval = Number.parseInt(announcement.dataset.wuvoAnnouncementInterval || '5000', 10);
  const interval = Number.isFinite(parsedInterval) ? Math.max(3000, parsedInterval) : 5000;
  let index = 0;
  let timer = 0;
  let resumeTimer = 0;

  track.style.width = `${slides.length * 100}%`;
  slides.forEach((slide) => {
    slide.style.flexBasis = `${100 / slides.length}%`;
  });

  const stop = () => {
    if (timer) window.clearInterval(timer);
    if (resumeTimer) window.clearTimeout(resumeTimer);
    timer = 0;
    resumeTimer = 0;
  };

  const update = () => {
    track.style.transform = `translate3d(${-index * (100 / slides.length)}%, 0, 0)`;
    track.style.transition = reducedMotion.matches ? 'none' : 'transform 520ms cubic-bezier(0.22, 0.61, 0.36, 1)';
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });
  };

  const start = () => {
    stop();
    if (reducedMotion.matches || document.hidden) return;
    timer = window.setInterval(() => {
      index = (index + 1) % slides.length;
      update();
    }, interval);
  };

  const schedule = () => {
    stop();
    if (reducedMotion.matches) return;
    resumeTimer = window.setTimeout(start, interval);
  };

  const goTo = (nextIndex) => {
    index = (nextIndex + slides.length) % slides.length;
    update();
    schedule();
  };

  previousButton?.addEventListener('click', () => goTo(index - 1));
  nextButton?.addEventListener('click', () => goTo(index + 1));
  announcement.addEventListener('mouseenter', stop);
  announcement.addEventListener('mouseleave', schedule);
  announcement.addEventListener('focusin', stop);
  announcement.addEventListener('focusout', schedule);

  const onVisibilityChange = () => {
    if (document.hidden) stop();
    else start();
  };
  const onMotionChange = () => {
    update();
    start();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  reducedMotion.addEventListener?.('change', onMotionChange);

  announcement.wuvoAnnouncementCleanup = () => {
    stop();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    reducedMotion.removeEventListener?.('change', onMotionChange);
  };

  update();
  start();
}

function initialiseCountdown(countdown) {
  if (countdown.dataset.countdownReady === 'true') return;
  countdown.dataset.countdownReady = 'true';

  const configuredEnd = countdown.dataset.countdownEnd.trim();
  const parsedEnd = configuredEnd ? new Date(configuredEnd) : null;
  const end = parsedEnd && !Number.isNaN(parsedEnd.getTime())
    ? parsedEnd
    : getDefaultCountdownEnd(new Date());

  const fields = {
    days: countdown.querySelector('[data-countdown-days]'),
    hours: countdown.querySelector('[data-countdown-hours]'),
    minutes: countdown.querySelector('[data-countdown-minutes]'),
    seconds: countdown.querySelector('[data-countdown-seconds]')
  };

  const update = () => {
    const remaining = Math.max(0, end.getTime() - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };

    Object.keys(values).forEach((key) => {
      if (fields[key]) fields[key].textContent = String(values[key]).padStart(2, '0');
    });

    if (remaining <= 0 && countdown.countdownTimer) {
      window.clearInterval(countdown.countdownTimer);
    }
  };

  update();
  countdown.countdownTimer = window.setInterval(update, 1000);
}

function initialiseCarousel(carousel) {
  if (carousel.dataset.wuvoCarouselReady === 'true') return;

  const track = carousel.querySelector('[data-wuvo-carousel-track]');
  const slides = Array.from(carousel.querySelectorAll('[data-wuvo-carousel-slide]'));
  if (!track || slides.length < 2) return;

  carousel.dataset.wuvoCarouselReady = 'true';

  const dots = Array.from(carousel.querySelectorAll('[data-wuvo-carousel-dot]'));
  const previousButton = carousel.querySelector('[data-wuvo-carousel-prev]');
  const nextButton = carousel.querySelector('[data-wuvo-carousel-next]');
  const status = carousel.querySelector('[data-wuvo-carousel-status]');
  const viewport = carousel.querySelector('.wuvo-how-carousel__viewport, .wuvo-inside-carousel__viewport') || carousel;
  const mobileOnly = carousel.dataset.wuvoCarouselMobile === 'true';
  const mobileMedia = window.matchMedia('(max-width: 900px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const autoplay = carousel.dataset.wuvoCarouselAutoplay === 'true';
  const pingPong = carousel.dataset.wuvoCarouselPingPong !== 'false';
  const parsedInterval = Number.parseInt(carousel.dataset.wuvoCarouselInterval || '3500', 10);
  const interval = Number.isFinite(parsedInterval) ? Math.max(2000, parsedInterval) : 3500;

  let index = 0;
  let direction = 1;
  let timer = 0;
  let resumeTimer = 0;
  let pointerStartX = null;
  let pointerStartY = null;
  let pointerId = null;

  const isActive = () => !mobileOnly || mobileMedia.matches;

  const stopAutoplay = () => {
    if (timer) {
      window.clearInterval(timer);
      timer = 0;
    }
    if (resumeTimer) {
      window.clearTimeout(resumeTimer);
      resumeTimer = 0;
    }
  };

  const update = ({ announce = false } = {}) => {
    const active = isActive();
    carousel.classList.toggle('is-wuvo-carousel-active', active);

    if (!active) {
      index = 0;
      direction = 1;
      track.style.removeProperty('transform');
      track.style.removeProperty('transition');
      slides.forEach((slide) => slide.removeAttribute('aria-hidden'));
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === 0);
        if (dotIndex === 0) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (status) status.textContent = '';
      return;
    }

    index = Math.max(0, Math.min(index, slides.length - 1));
    track.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
    track.style.transition = reducedMotion.matches
      ? 'none'
      : 'transform 620ms cubic-bezier(0.22, 0.61, 0.36, 1)';

    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });

    dots.forEach((dot, dotIndex) => {
      const current = dotIndex === index;
      dot.classList.toggle('is-active', current);
      if (current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    carousel.dataset.wuvoCarouselIndex = String(index);
    if (status && announce) {
      status.textContent = `Slide ${index + 1} of ${slides.length}`;
    }
  };

  const startAutoplay = () => {
    stopAutoplay();
    if (!isActive() || !autoplay || reducedMotion.matches || document.hidden) return;

    timer = window.setInterval(() => {
      if (pingPong) {
        if (index >= slides.length - 1) direction = -1;
        if (index <= 0) direction = 1;
        index += direction;
      } else {
        index = (index + 1) % slides.length;
      }
      update();
    }, interval);
  };

  const scheduleAutoplay = () => {
    stopAutoplay();
    if (!isActive() || !autoplay || reducedMotion.matches) return;
    resumeTimer = window.setTimeout(startAutoplay, interval);
  };

  const goTo = (newIndex, { announce = true, restart = true } = {}) => {
    if (!isActive()) return;
    index = (newIndex + slides.length) % slides.length;
    if (index === 0) direction = 1;
    if (index === slides.length - 1) direction = -1;
    update({ announce });
    if (restart) scheduleAutoplay();
  };

  previousButton?.addEventListener('click', () => goTo(index - 1));
  nextButton?.addEventListener('click', () => goTo(index + 1));
  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      const dotIndex = Number.parseInt(dot.dataset.wuvoCarouselIndex || '0', 10);
      goTo(Number.isFinite(dotIndex) ? dotIndex : 0);
    });
  });

  const onPointerDown = (event) => {
    if (!isActive() || event.pointerType === 'mouse') return;
    pointerStartX = event.clientX;
    pointerStartY = event.clientY;
    pointerId = event.pointerId;
    stopAutoplay();
  };

  const onPointerEnd = (event) => {
    if (pointerStartX === null || pointerStartY === null || (pointerId !== null && event.pointerId !== pointerId)) return;

    const deltaX = event.clientX - pointerStartX;
    const deltaY = event.clientY - pointerStartY;
    pointerStartX = null;
    pointerStartY = null;
    pointerId = null;

    if (Math.abs(deltaX) >= 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
      goTo(index + (deltaX < 0 ? 1 : -1));
      return;
    }

    scheduleAutoplay();
  };

  viewport.addEventListener('pointerdown', onPointerDown, { passive: true });
  viewport.addEventListener('pointerup', onPointerEnd, { passive: true });
  viewport.addEventListener('pointercancel', onPointerEnd, { passive: true });

  carousel.addEventListener('focusin', stopAutoplay);
  carousel.addEventListener('focusout', scheduleAutoplay);
  carousel.addEventListener('mouseenter', stopAutoplay);
  carousel.addEventListener('mouseleave', scheduleAutoplay);

  const onMediaChange = () => {
    stopAutoplay();
    update();
    startAutoplay();
  };

  mobileMedia.addEventListener?.('change', onMediaChange);
  reducedMotion.addEventListener?.('change', onMediaChange);

  const onVisibilityChange = () => {
    if (document.hidden) stopAutoplay();
    else startAutoplay();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  carousel.wuvoCarouselGoTo = goTo;
  carousel.wuvoCarouselCleanup = () => {
    stopAutoplay();
    mobileMedia.removeEventListener?.('change', onMediaChange);
    reducedMotion.removeEventListener?.('change', onMediaChange);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  update();
  startAutoplay();
}

function initialiseWuvo(root) {
  root.querySelectorAll('[data-wuvo-announcement]').forEach(initialiseAnnouncement);
  root.querySelectorAll('[data-wuvo-countdown]').forEach(initialiseCountdown);
  root.querySelectorAll('[data-wuvo-carousel]').forEach(initialiseCarousel);
}

initialiseWuvo(document);

document.addEventListener('shopify:section:load', (event) => {
  initialiseWuvo(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-wuvo-announcement]').forEach((announcement) => {
    announcement.wuvoAnnouncementCleanup?.();
  });
  event.target.querySelectorAll('[data-wuvo-carousel]').forEach((carousel) => {
    carousel.wuvoCarouselCleanup?.();
  });
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-wuvo-carousel-slide]');
  const carousel = slide?.closest('[data-wuvo-carousel]');
  if (!slide || !carousel || !carousel.wuvoCarouselGoTo) return;

  const slides = Array.from(carousel.querySelectorAll('[data-wuvo-carousel-slide]'));
  const selectedIndex = slides.indexOf(slide);
  if (selectedIndex >= 0) carousel.wuvoCarouselGoTo(selectedIndex, { announce: false, restart: false });
});

function updateWuvoDevelopmentScale() {
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  const scale = viewportWidth > 900 && viewportWidth < 1600
    ? viewportWidth / 1440
    : 1;

  document.documentElement.style.setProperty(
    '--wuvo-development-scale',
    scale.toFixed(8)
  );
}

updateWuvoDevelopmentScale();

let wuvoScaleFrame = 0;
window.addEventListener('resize', () => {
  window.cancelAnimationFrame(wuvoScaleFrame);
  wuvoScaleFrame = window.requestAnimationFrame(updateWuvoDevelopmentScale);
}, { passive: true });

document.addEventListener('shopify:section:load', updateWuvoDevelopmentScale);
